// ---------- การคำนวณสัญญาเช่า (ตรงกับ RPC rp_open_contract ฝั่ง DB) ----------
// ราคาใน catalog รวม VAT แล้ว · มัดจำตามระดับ เพดาน 10,000/ชิ้น ·
// คืนเพิ่ม +35% ของเรททริปต่อคืน · ส่วนลดปริมาณเฉพาะกองถ่าย (ลดที่ส่วนลด ไม่ลดเรทฐาน) ·
// WHT 5% เฉพาะนิติบุคคล จากฐานก่อน VAT · มัดจำไม่อยู่ในฐาน WHT

// กาง "เลือกไว้กี่ชิ้นต่อรุ่น" (Map<sku, จำนวน>) เป็น 1 แถวต่อชิ้น พร้อมจับคู่ตัวจริงที่จะจอง
// ตัวคำนวณและ RPC rp_open_contract คิดทีละชิ้นเสมอ — ส่วนลดปริมาณและมัดจำจึงนับถูกเอง
// จำนวนถูกหั่นตามของว่างจริงเสมอ (slice) กันกรณีวันที่เปลี่ยนแล้วของว่างลดลง
export function expandPicked(picked, catalog, availMap) {
  const out = [];
  for (const [sku, qty] of picked) {
    const c = catalog.find(x => x.sku === sku);
    if (!c) continue;
    for (const u of (availMap.get(sku) || []).slice(0, qty)) {
      out.push({ ...c, unit_id: u.id, unit_code: u.unit_code });
    }
  }
  return out;
}

// รวมบรรทัดของรุ่นเดียวกันให้เหลือบรรทัดเดียว + จำนวน — ใช้แสดงผลเท่านั้น (ช่องสรุป + ใบเช่า)
// ค่าเช่า/เงินประกันในบรรทัดที่รวมแล้ว = ยอดรวมของทุกชิ้นในรุ่นนั้น
export function groupLines(lines) {
  const m = new Map();
  for (const l of lines) {
    const g = m.get(l.sku);
    if (!g) {
      m.set(l.sku, { ...l, qty: 1, unit_codes: l.unit_code ? [l.unit_code] : [] });
      continue;
    }
    g.qty += 1;
    g.rent += l.rent;
    g.deposit += l.deposit;
    if (l.unit_code) g.unit_codes.push(l.unit_code);
  }
  return [...m.values()];
}

export function calcContract({ items, nights, tier, entity, vatRate, whtRate, settings, volumeDiscounts }) {
  const extraPct = Number(settings?.extra_night_pct ?? 0.35);
  const depCap = Number(settings?.deposit_cap ?? 10000);
  const n = Math.max(1, nights || 2);
  const extra = n > 2 ? (n - 2) * extraPct : 0;

  let gross = 0, dep = 0, capped = false;
  const lines = items.map((c) => {
    const rent = Math.round(c.trip_rate * (1 + extra) * Number(tier.rent_mul));
    const trip = Math.round(c.trip_rate * Number(tier.rent_mul));
    let d = Math.round(c.retail * Number(tier.dep_pct));
    if (tier.dep_waive_under != null && Number(c.retail) <= Number(tier.dep_waive_under)) d = 0;
    const wasCapped = d > depCap;
    if (wasCapped) { d = depCap; capped = true; }
    gross += rent; dep += d;
    return { ...c, rent, trip, deposit: d, capped: wasCapped };
  });

  const vol = (volumeDiscounts || [])
    .filter(v => v.tier_key === tier.key && items.length >= v.min_items)
    .sort((a, b) => b.min_items - a.min_items)[0] || null;
  const volOff = vol ? Number(vol.off_pct) : 0;
  const volAmt = Math.round(gross * volOff);
  const net = gross - volAmt;
  const exVat = Math.round(net / (1 + vatRate));
  const vat = net - exVat;
  const isCo = entity === 'company';
  const wht = isCo ? Math.round(exVat * whtRate) : 0;
  const cash = net - wht + dep;

  return { lines, nights: n, gross, vol, volOff, volAmt, net, exVat, vat, wht, dep, capped, cash, isCo };
}

// ---------- ค่าเสียหาย 4 ระดับ (ฐาน = ราคาป้าย ณ วันทำสัญญา) ----------
export const COND = [
  { n: 'ปกติ', fee: () => 0, hint: '' },
  { n: 'สกปรกมาก', fee: () => 300, hint: '200–500 บาท/ชิ้น', min: 200, max: 500 },
  { n: 'ซ่อมได้', fee: (retail) => Math.round(retail * 0.1), hint: 'ตามจริง ไม่เกิน 30% ของราคาป้าย', maxPct: 0.30 },
  { n: 'พัง/หาย', fee: (retail) => Math.round(retail * 0.6), hint: '60% ของราคาป้าย', fixed: 0.60 },
];

// ---------- trade-in ----------
export function calcTradein({ cat, grade, hasHistory, newBill, grades, tradeCapPct = 0.40 }) {
  const order = ['A', 'B', 'C'];
  const effKey = hasHistory ? grade : order[Math.min(order.indexOf(grade) + 1, 2)];
  const g = grades.find(x => x.key === effKey);
  const credit = Math.round(cat.retail * Number(g.in_pct) / 100) * 100;
  const resale = Math.round(cat.retail * Number(g.out_pct) / 100) * 100;
  const cap = Math.round((newBill || 0) * tradeCapPct);
  const capped = credit > cap;
  const applied = capped ? cap : credit;
  const cash = Math.max(0, (newBill || 0) - applied);
  return { effKey, g, credit, resale, cap, capped, applied, cash, margin: resale - applied };
}
